// Accessibility audit utility — pure functions, no Stimulus dependency.
// Mounted by the style-audit Stimulus controller (which renders the panel
// chrome) and called from any context that wants to predict whether a
// chosen style combination will read on a small phone screen.
//
// All checks are GUIDANCE, not gates — `auditStyle(...)` returns
// `{ warnings: [...], info: [...] }` arrays. Callers decide how to
// surface them. Callers MUST NOT block save actions on a non-empty
// warnings array; the renderer accepts every legal style combination.
//
// WCAG 2.1 contrast ratios are computed via relative luminance:
//   L = 0.2126 * R' + 0.7152 * G' + 0.0722 * B'
// where each channel is gamma-corrected from sRGB:
//   c' = (c/255) <= 0.03928
//          ? (c/255) / 12.92
//          : (((c/255) + 0.055) / 1.055) ^ 2.4
// Ratio between two colours = (L1 + 0.05) / (L2 + 0.05), L1 >= L2.
//
// Thresholds are the published WCAG AA values:
//   - Body text: 4.5:1
//   - Large/display text (>= 24 px or 18.66 px bold): 3:1
// We treat anything from the audit caller as "display" if it's the
// display element's size; the caller decides which axis a given font
// size lives on.

// ────────────────────────────────────────────────────────────────────
// Contrast math (WCAG 2.1)
// ────────────────────────────────────────────────────────────────────

// Parses a hex colour string ("#RRGGBB" or "#RGB") into {r, g, b} in
// the 0..255 range. Returns null for invalid input (caller treats this
// as "skip the check" — do not warn on malformed palette data).
export function parseHexColor(hex) {
  if (typeof hex !== "string") return null
  const trimmed = hex.trim().replace(/^#/, "")
  if (!/^[0-9a-fA-F]+$/.test(trimmed)) return null
  let r, g, b
  if (trimmed.length === 3) {
    r = parseInt(trimmed[0] + trimmed[0], 16)
    g = parseInt(trimmed[1] + trimmed[1], 16)
    b = parseInt(trimmed[2] + trimmed[2], 16)
  } else if (trimmed.length === 6) {
    r = parseInt(trimmed.slice(0, 2), 16)
    g = parseInt(trimmed.slice(2, 4), 16)
    b = parseInt(trimmed.slice(4, 6), 16)
  } else {
    return null
  }
  return { r, g, b }
}

// sRGB gamma correction for a single channel — input 0..255, output
// 0..1 in linear-light.
function linearise(channel) {
  const c = channel / 255
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

// Relative luminance per WCAG 2.1 §1.4.3, returning 0..1.
export function relativeLuminance(rgb) {
  if (!rgb) return null
  const r = linearise(rgb.r)
  const g = linearise(rgb.g)
  const b = linearise(rgb.b)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

// Contrast ratio between two hex colours. Returns null if either
// colour can't be parsed. Identical colours return 1; max possible is
// 21 (black on white).
export function contrastRatio(hexA, hexB) {
  const a = relativeLuminance(parseHexColor(hexA))
  const b = relativeLuminance(parseHexColor(hexB))
  if (a == null || b == null) return null
  const [hi, lo] = a >= b ? [a, b] : [b, a]
  return (hi + 0.05) / (lo + 0.05)
}

// ────────────────────────────────────────────────────────────────────
// Audit
// ────────────────────────────────────────────────────────────────────

// WCAG AA thresholds. Body needs 4.5:1; display/large can rely on 3:1.
export const WCAG_BODY_AA = 4.5
export const WCAG_DISPLAY_AA = 3.0

// Below these sizes (in renderer pixels — i.e. 1080×1920 canvas pixels)
// text becomes hard to read on the typical phone-in-hand viewing
// context. Values chosen against the existing kit-template defaults:
// most templates land display ~80px+, body ~32px+, so 32/18 are the
// "you've scaled this down too far" floor.
export const MIN_DISPLAY_PX = 32
export const MIN_BODY_PX = 18

// Audits a chosen style hash and returns { warnings, info } arrays.
// Caller passes only what it has — every parameter is optional and a
// missing value silently skips its check rather than warning.
//
// Signature is intentionally flat (not nested) so Stimulus controllers
// can read field values straight off form inputs and pass them in.
export function auditStyle({
  paletteText,
  paletteBg,
  strokeWidth,
  strokeColor,
  displayFontSizePx,
  bodyFontSizePx,
  italicOverride,
  pairDefaultDisplayItalic,
} = {}) {
  const warnings = []
  const info = []

  // Resolve italic-on-display. Mirror of resolveItalic in
  // utils/text_treatment.js — duplicated here so this module has zero
  // imports and can be unit-tested in isolation.
  const displayItalic = italicOverride === "italic"
    ? true
    : italicOverride === "upright"
      ? false
      : !!pairDefaultDisplayItalic

  // 1. Text vs background contrast — split into display + body axes
  //    because WCAG AA permits a lower bar for large text. We surface
  //    BOTH a body-axis warning (if applicable) and a display-axis
  //    warning (if applicable) so creators see the strongest signal.
  let textBgRatio = null
  if (paletteText && paletteBg) {
    textBgRatio = contrastRatio(paletteText, paletteBg)
    if (textBgRatio != null) {
      info.push({
        id: "contrast_ratio",
        label: `Text/background contrast: ${textBgRatio.toFixed(2)}:1`,
        detail: `WCAG AA recommends ≥ ${WCAG_BODY_AA}:1 for body text and ≥ ${WCAG_DISPLAY_AA}:1 for large display text.`,
      })

      if (textBgRatio < WCAG_DISPLAY_AA) {
        warnings.push({
          id: "contrast_display_low",
          severity: "warning",
          label: "Display text contrast is below WCAG AA",
          detail: `Your text colour against the background measures ${textBgRatio.toFixed(2)}:1 — display text should be at least ${WCAG_DISPLAY_AA}:1. Consider raising contrast or adding a darker overlay/tint to the background.`,
        })
      } else if (textBgRatio < WCAG_BODY_AA) {
        warnings.push({
          id: "contrast_body_low",
          severity: "warning",
          label: "Body text contrast is below WCAG AA",
          detail: `Your text colour against the background measures ${textBgRatio.toFixed(2)}:1 — body text should be at least ${WCAG_BODY_AA}:1. Display text passes, but smaller body copy may be hard to read on a phone screen.`,
        })
      }
    }
  }

  // 2. Display font size after scale (caller multiplies by font_size_scale
  //    before passing in — keeps the audit decoupled from the scale knob).
  if (Number.isFinite(displayFontSizePx) && displayFontSizePx > 0) {
    if (displayFontSizePx < MIN_DISPLAY_PX) {
      warnings.push({
        id: "display_too_small",
        severity: "warning",
        label: "Display text may be too small to read",
        detail: `Display text is sized at ${Math.round(displayFontSizePx)}px on a 1080-wide canvas — under ${MIN_DISPLAY_PX}px is hard to read at TikTok / Reels viewing distances. Try raising the font-size scale.`,
      })
    }
  }

  // 3. Body font size after scale.
  if (Number.isFinite(bodyFontSizePx) && bodyFontSizePx > 0) {
    if (bodyFontSizePx < MIN_BODY_PX) {
      warnings.push({
        id: "body_too_small",
        severity: "warning",
        label: "Body text may be too small to read",
        detail: `Body text is sized at ${Math.round(bodyFontSizePx)}px on a 1080-wide canvas — under ${MIN_BODY_PX}px disappears on small phone screens. Raise the font-size scale or simplify the body copy.`,
      })
    }
  }

  // 4. Stroke colour matches text colour — defeats the purpose of the
  //    stroke (zero contrast at the outline). Only an issue when stroke
  //    is actually painted (width > 0).
  if (Number.isFinite(strokeWidth) && strokeWidth > 0 && strokeColor && paletteText) {
    if (sameColour(strokeColor, paletteText)) {
      warnings.push({
        id: "stroke_matches_text",
        severity: "warning",
        label: "Stroke colour matches text colour",
        detail: "Your stroke is painted in the same colour as the text — it adds weight but no contrast outline. Pick a stroke colour that contrasts with the text (typically black or a deep version of your background) for caption-style legibility.",
      })
    }
  }

  // 5. Italic display + low body contrast — italic display compounds
  //    reading effort, especially when body legibility is already weak.
  //    Trigger only when both axes degrade together (don't double-warn
  //    when contrast alone already fired a strong warning).
  if (displayItalic && textBgRatio != null && textBgRatio < WCAG_BODY_AA && textBgRatio >= WCAG_DISPLAY_AA) {
    warnings.push({
      id: "italic_display_low_body_contrast",
      severity: "warning",
      label: "Italic display + low body contrast compounds reading effort",
      detail: "Italic display text already reads slower than upright; combined with body text below WCAG AA, viewers may struggle. Consider 'upright' italic override or a higher-contrast text colour.",
    })
  }

  return { warnings, info }
}

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────

// Compare two hex colours after normalising to lowercase 6-char hex.
// Returns false on parse failure (we don't want to false-positive a
// "matches" warning on garbage input).
function sameColour(a, b) {
  const rgbA = parseHexColor(a)
  const rgbB = parseHexColor(b)
  if (!rgbA || !rgbB) return false
  return rgbA.r === rgbB.r && rgbA.g === rgbB.g && rgbA.b === rgbB.b
}
